import * as basket from '@easterngraphics/wcf/modules/eaiws/basket/index';
import { isNotNullOrEmpty, isNullOrEmpty, trimRight, trimLeft } from '@easterngraphics/wcf/modules/utils/string';

/** @internal */
export class ObxReader {
    public get isAvailable(): boolean {
        return this.mDoc != null;
    }

    public constructor() {
        this.mDoc = null;
        this.mItemRoot = null;
        this.mLanguages = ['en'];
        this.mManufacturerNameMap = new Map();
    }

    public getArticleProperties(elementData: ElementData | null): Array<basket.ArticleProperties> {
        const result = new Array<basket.ArticleProperties>();
        if (elementData?.article) {
            const ap = new basket.ArticleProperties();
            ap.manufacturer = elementData.article.manufacturer ?? '';
            ap.manufacturerId = elementData.article.manufacturerId ?? '';
            ap.manufacturerName = elementData.article.manufacturerName ?? '';
            ap.program = elementData.article.program ?? '';
            ap.seriesId = elementData.article.seriesId ?? '';
            ap.seriesName = elementData.article.seriesName ?? '';
            ap.baseArticleNumber = elementData.article.baseArtNum ?? '';
            ap.finalArticleNumber = elementData.article.finalArtNum ?? '';
            ap.variantCode = elementData.article.varCode ?? '';
            ap.shortText = elementData.article.shortText ?? '';
            ap.longText = elementData.article.longText ?? '';
            ap.distributionRegion = elementData.article.distributionRegion ?? '';
            ap.currency = elementData.article.currency ?? '';
            result.push(ap);
        }
        return result;
    }

    private mDoc: Element | null;
    private mItemRoot: Element | null;

    private mLanguages: Array<string>;
    private mManufacturerNameMap: Map<string, string>;

    private getChildElement(pParent: Element | null, pTag: string): Element | null {
        if (pParent == null) return null;

        for (let i = 0; i < pParent.children.length; ++i) {
            const tChild = pParent.children.item(i);
            if (tChild != null && tChild.tagName === pTag) return tChild;
        }
        return null;
    }

    private getChildElements(pParent: Element | null, pTag: string): Array<Element> {
        const tElements = new Array<Element>();

        if (pParent != null) {
            for (let i = 0; i < pParent.children.length; ++i) {
                const tChild = pParent.children.item(i);
                if (tChild != null && tChild.tagName === pTag) tElements.push(tChild);
            }
        }
        return tElements;
    }

    private queryChildElement(pParent: Element | null, pSelector: string): Element | null {
        if (pParent == null) return null;

        const tElements = pParent.querySelectorAll(pSelector);
        for (let i = 0; i < tElements.length; ++i) {
            const tElement = tElements.item(i);
            if (tElement.parentNode === pParent) return tElement;
        }
        return null;
    }

    // eslint-disable-next-line complexity
    public getArticleElementData(pBasketId: string, pElement: Element): ElementData | null {
        if (pElement == null) return null;

        let tElement: Element | null;

        const tData = {} as ElementData;
        const tType = pElement.getAttribute('itemType');
        switch (tType) {
            case 'BasketAggregate':
                tData.itemType = 'Aggregate';
                break;
            case 'BasketPartialPlanning':
                tData.itemType = 'PartialPlanning';
                break;
            case 'UserArticle':
                tData.itemType = 'UserArticle';
                break;
            default:
                tData.itemType = 'Article';
        }
        const tAData = {} as ArticleElementData;
        tData.article = tAData;
        tElement = this.getChildElement(pElement, 'manufacturer');
        if (tElement != null) {
            tAData.manufacturerId = tElement.getAttribute('id');
            tAData.manufacturerName = this.getTextElementContent('name', tElement);
            if (tAData.manufacturerId != null) {
                if (isNullOrEmpty(tAData.manufacturerName)) {
                    tAData.manufacturerName = this.mManufacturerNameMap.get(tAData.manufacturerId);
                } else {
                    this.mManufacturerNameMap.set(tAData.manufacturerId, tAData.manufacturerName);
                }
            }
        }
        this.readPdInfo(pElement, tAData);

        //series
        tElement = this.getChildElement(pElement, 'series');
        if (tElement != null) {
            tAData.seriesId = tElement.getAttribute('id');
            tAData.seriesName = this.getTextElementContent('name', tElement);
            if (tAData.manufacturerId != null && tAData.seriesId != null) {
                const tMapId = tAData.manufacturerId + '_' + tAData.seriesId;
                if (isNullOrEmpty(tAData.seriesName)) {
                    tAData.seriesName = this.mManufacturerNameMap.get(tMapId);
                } else {
                    this.mManufacturerNameMap.set(tMapId, tAData.seriesName);
                }
            }
        }

        //special and Additioanl text
        const appData = this.getChildElement(pElement, 'appData');
        const pConBasketApp = appData ? this.queryChildElement(appData, "application[key='pCon.basket']") : null;
        const artTextSet = pConBasketApp ? this.getChildElement(pConBasketApp, 'artTextSet') : null;

        if (artTextSet) {
            const textGroups = this.getChildElements(artTextSet, 'textGroup');
            for (const textGroup of textGroups) {
                const groupId = textGroup.getAttribute('id');
                const visible = textGroup.getAttribute('visible') === '1';
                const text = this.getLangText('text', textGroup);

                if (!text || !visible) continue;

                if (groupId === '@add') {
                    tAData.additionalText = text;
                } else if (groupId === '71') {
                    tAData.specialText = text;
                }
            }
        }

        //short text
        tElement = this.queryChildElement(pElement, "description[type='short']");
        if (tElement != null) {
            tAData.shortText = this.getLangText('text', tElement);
            tData.label = tAData.shortText;
        }

        //long text
        tElement = this.queryChildElement(pElement, "description[type='long']");
        if (tElement != null) {
            tAData.longText = this.getLangText('text', tElement);
            if (tElement.getAttribute('default') === '1') tData.label = tAData.longText;
        }

        //feature text
        tElement = this.queryChildElement(pElement, "description[type='features']");
        if (tElement != null) {
            tAData.featureText = this.getLangText('text', tElement);
        }

        //base article number
        tElement = this.queryChildElement(pElement, "artNr[type='base']");
        if (tElement != null) {
            tAData.baseArtNum = tElement.textContent;
        }

        //final article number
        tElement = this.queryChildElement(pElement, "artNr[type='final']");
        if (tElement != null) {
            tAData.finalArtNum = tElement.textContent;
            tAData.useFinalArtNum = tElement.getAttribute('default') === '1';
        }

        //var code
        tElement = this.queryChildElement(pElement, "artNr[type='varcode']");
        if (tElement != null) {
            tAData.varCode = tElement.textContent;
        }

        //ofml var code
        tElement = this.queryChildElement(pElement, "artNr[type='ofmlvarcode']");
        if (tElement != null) {
            tAData.ofmlVarCode = tElement.textContent;
        }

        //quantity
        tAData.quantity = 1;
        tElement = this.getChildElement(pElement, 'quantity');
        if (tElement != null) {
            tAData.quantity = parseFloat(tElement.getAttribute('count')!);
        }

        //pd sales price
        tElement = this.queryChildElement(pElement, "itemPrice[type='sale'][pd='1']");
        if (tElement != null) {
            tAData.pdSalesPrice = parseFloat(tElement.getAttribute('value')!);
            tAData.currency = tElement.getAttribute('currency');
        }

        //pd purchase price
        tElement = this.queryChildElement(pElement, "itemPrice[type='purchase'][pd='1']");
        if (tElement != null) {
            tAData.pdSalesPrice = parseFloat(tElement.getAttribute('value')!);
            tAData.currency = tElement.getAttribute('currency');
        }

        //alternativePosition
        tElement = this.getChildElement(this.getChildElement(pElement, 'exclOffers'), 'exclOffer');
        tAData.alternativePosition = tElement != null;

        //inconsistency
        const tElements = this.getChildElements(this.getChildElement(pElement, 'inconsistency'), 'text');
        if (tElements.length > 0) tAData.inconsistencyReason = [];
        for (const tText of tElements) {
            if (tText.textContent == null) {
                continue;
            }
            tAData.inconsistencyReason!.push(tText.textContent);
        }
        tAData.inconsistencyFlag = tAData.inconsistencyReason != null && tAData.inconsistencyReason.length > 0;

        //geometry id (aggregated checksum has prioririty)
        tElement = this.queryChildElement(pElement, "geometryChecksum[aggr='1']");
        if (tElement == null) tElement = this.getChildElement(pElement, 'geometryChecksum');

        if (tElement != null) {
            tAData.geometryId = tElement.getAttribute('value');

            //add transform checksum to id
            tElement = this.getChildElement(pElement, 'geoTransformCksum');
            if (tElement != null && tAData.geometryId != null) {
                //checksum is base64 encoded
                const tBinGeomChks = atob(tAData.geometryId);
                const tBinTransChks = atob(tElement.getAttribute('value')!);
                const tGeomId = btoa(tBinGeomChks + tBinTransChks);
                tAData.geometryId = trimRight(tGeomId, '='); //remove padding char
            }
        }
        //convert base64 string to url format
        if (!isNullOrEmpty(tAData.geometryId)) {
            tAData.geometryId = tAData.geometryId.replace(/\//g, '_');
            tAData.geometryId = tAData.geometryId.replace(/\+/g, '-');
        }

        //main article id
        tAData.mainArticleId = '00000000-0000-0000-0000-000000000000';
        tElement = this.getChildElement(pElement, 'mainItem');
        if (tElement != null) {
            const tId = tElement.getAttribute('id');
            if (tId != null) tAData.mainArticleId = tId;
        }

        //sub article ids
        tAData.subArticleIds = this.getSubArticleIds(pBasketId);
        return tData;
    }

    private getTextElementContent(pTagName: string, pElement: Element): string | null {
        const tText = this.getTextElement(pTagName, pElement);
        return tText != null ? tText.content : null;
    }
    private getTextElement(pTagName: string, pElement: Element): TextElement | null {
        if (pElement == null) return null;

        for (const tLang of this.mLanguages) {
            const tText = this.queryChildElement(pElement, pTagName + "[lang='" + tLang + "']");
            if (tText != null && tText.textContent != null) {
                return { content: tText.textContent, language: tLang };
            }
        }

        //try undefined language
        let tText = this.queryChildElement(pElement, pTagName + "[lang='und']");
        if (tText != null && tText.textContent != null) {
            return { content: tText.textContent, language: 'und' };
        }

        //try undefined language for secondary tag (necessary for text manager texts e.g. additional text)
        tText = this.queryChildElement(pElement, pTagName + '\\.2' + "[lang='und']");
        if (tText != null && tText.textContent != null) {
            return { content: tText.textContent, language: 'und' };
        }

        //try empty language
        tText = this.queryChildElement(pElement, pTagName + "[lang='']");
        if (tText != null && tText.textContent != null) {
            return { content: tText.textContent };
        }

        //use first
        tText = this.getChildElement(pElement, pTagName);
        if (tText != null && tText.textContent != null) {
            return { content: tText.textContent, language: tText.getAttribute('lang') ?? undefined };
        }

        return null;
    }

    private getLangText(pTagName: string, pElement: Element): string | null {
        if (pElement == null) return null;

        for (const tLang of this.mLanguages) {
            const tText = this.queryChildElement(pElement, pTagName + '[lang="' + tLang + '"]');
            if (tText != null) return tText.textContent;
        }

        let tText = this.queryChildElement(pElement, pTagName + '[lang=""]');
        if (tText != null) return tText.textContent;

        //use first
        tText = this.getChildElement(pElement, pTagName);
        if (tText != null) return tText.textContent;

        return null;
    }

    private getSubArticleIds(pBasketId: string): Array<string> | null {
        if (this.mItemRoot == null) return null;

        const tItemIds = new Array<string>();
        const tElements = this.mItemRoot.querySelectorAll("[basketId] > mainItem[id='" + pBasketId + "']");
        for (let i = 0; i < tElements.length; ++i) {
            const tParent = tElements.item(i).parentElement;
            if (tParent != null) {
                const tBasketId = tParent.getAttribute('basketId');
                if (isNotNullOrEmpty(tBasketId)) tItemIds.push(tBasketId);
            }
        }
        return tItemIds;
    }
    private readPdInfo(pElement: Element, pData: ArticleElementData): void {
        const tPdInfoEl = this.getChildElement(pElement, 'pdInfo');
        if (tPdInfoEl != null) {
            pData.distributionRegion = tPdInfoEl.getAttribute('region');

            //prog id looks like this: @man_prg
            const tProgId = tPdInfoEl.getAttribute('progId');
            if (tProgId != null && tProgId.startsWith('@')) {
                const tTrimmedId = trimLeft(tProgId, '@');
                const tParts = tTrimmedId.split('_');
                if (tParts.length > 1) {
                    pData.manufacturer = tParts[0];
                    pData.program = tParts[1];
                }
            }
        }
    }
}

interface TextElement {
    content: string;
    language?: string;
}

interface ElementData {
    itemId: string;
    itemType: basket.BasketItemType;
    label?: string | null;
    article?: ArticleElementData;
    tmRows?: Array<basket.TMRow>;
    composableGeometry: basket.ComposableGeometryProperties | null;
}

interface ArticleElementData {
    manufacturerId?: string | null;
    manufacturerName?: string | null;
    seriesId?: string | null;
    seriesName?: string | null;

    manufacturer?: string | null;
    program?: string | null;
    distributionRegion?: string | null;

    shortText?: string | null;
    longText?: string | null;
    featureText?: string | null;
    additionalText?: string | null;
    specialText?: string | null;

    baseArtNum?: string | null;
    finalArtNum?: string | null;
    varCode?: string | null;
    ofmlVarCode?: string | null;
    useFinalArtNum?: boolean | null;

    quantity?: number | null;
    currency?: string | null;
    pdPurchasePrice?: number | null;
    pdSalesPrice?: number | null;
    alternativePosition?: boolean | null;

    inconsistencyFlag?: boolean | null;
    inconsistencyReason?: Array<string> | null;

    geometryId?: string | null;

    mainArticleId: string;
    subArticleIds?: Array<string> | null;
    isModel?: boolean;
}
